// NaturalisticDesignSection.js
import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/naturalisticDesign.css';

const NaturalisticDesignSection = ({ layout = 'full' }) => {
  
  const coreFeatures = [
    {
      title: "Water Conservation Excellence",
      subtitle: "Drought-Tolerant Design",
      text: "Strategic plant selection and placement reduces irrigation needs by 40-60%. Our designs utilize native and adapted species that thrive in Idaho's climate with minimal supplemental watering once established.",
      details: [
        "Deep-rooted prairie grasses for slope stabilization",
        "Xeric plant palettes for sunny exposures",
        "Rain garden integration for natural drainage",
        "Smart irrigation zoning for mixed landscapes"
      ]
    },
    {
      title: "Plant Health & Sustainable Care",
      subtitle: "Green vs. Green Philosophy",
      text: "Not all green is created equal. We focus on plant health over chemical dependency, creating resilient landscapes that maintain their beauty through natural processes.",
      details: [
        "Soil health improvement strategies",
        "Natural pest and disease resistance",
        "Reduced chemical inputs",
        "Long-term vigor through proper plant selection"
      ]
    },
    {
      title: "Prairie & Perennial Meadows",
      subtitle: "Natural Beauty with Purpose",
      text: "Transform unused lawn areas into dynamic prairie landscapes. These naturalistic plantings provide year-round interest while significantly reducing maintenance requirements.",
      details: [
        "Native Idaho grasses and wildflowers",
        "Seasonal color progression",
        "Wildlife habitat creation",
        "Once-yearly maintenance schedule"
      ]
    },
    {
      title: "Fire-Wise Landscaping",
      subtitle: "Safety Through Smart Design",
      text: "Protect your property with defensible space strategies. From proper plant spacing to material selection, we create beautiful landscapes that reduce fire risk.",
      details: [
        "Zone-based planting strategies",
        "Fire-resistant plant selection",
        "Proper mulch alternatives to wood chips",
        "Hardscape integration for fire breaks"
      ]
    }
  ];

  const specialtyFeatures = [
    {
      title: "Dry Stack Stone Features",
      text: "Expert craftsmanship in traditional dry stack construction creates lasting beauty without mortar. Perfect for retaining walls, garden borders, and architectural features."
    },
    {
      title: "Naturalistic Playgrounds",
      text: "Move beyond traditional play equipment. We design nature-based play spaces using boulders, logs, sand, and plantings that encourage imaginative outdoor exploration."
    }
  ];

  // Compact layout for Property Management page
  if (layout === 'compact') {
    return (
      <section className="naturalistic-compact bg-clean-snow">
        <div className="section-inner">
          <h2 className="section-title text-center">FORWARD-THINKING LANDSCAPE SOLUTIONS</h2>
          <p className="section-subtitle text-center">
            Reduce long-term costs while enhancing property value through sustainable design
          </p>
          <div className="compact-features">
            <div className="compact-feature">
              <h4>40-60% Water Reduction</h4>
              <p>Drought-tolerant designs that maintain beauty with less irrigation</p>
            </div>
            <div className="compact-feature">
              <h4>Lower Maintenance Costs</h4>
              <p>Prairie and meadow areas require just annual maintenance</p>
            </div>
            <div className="compact-feature">
              <h4>Enhanced Property Value</h4>
              <p>Modern buyers seek sustainable, low-maintenance landscapes</p>
            </div>
          </div>
          <Link to="/landscape#naturalistic" className="compact-learn-more">
            EXPLORE NATURALISTIC DESIGN OPTIONS
          </Link>
        </div>
      </section>
    );
  }

  // Teaser layout for Homepage
  if (layout === 'teaser') {
    return (
      <section className="naturalistic-teaser">
        <div className="teaser-content">
          <div className="teaser-image">
            <img src="/images/landscape/naturalDesign.JPG" alt="Beautiful perennial meadow landscape" />
          </div>
          <div className="teaser-text">
            <h3>Beautiful Landscapes</h3>
            <h4>Beautiful Landscapes That Work With Nature</h4>
            <p>
              Discover how prairie grasses, native perennials, and thoughtful design 
              can create stunning landscapes that require less water, less maintenance, 
              and provide more seasonal interest than traditional lawns.
            </p>
            <Link to="/landscape#naturalistic" className="teaser-link">
              LEARN MORE ABOUT SUSTAINABLE LANDSCAPING
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Full layout for Landscape page
  return (
    <div className="naturalistic-full">
      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-inner">
          <h2 className="section-title text-center">NATURALISTIC DESIGN APPROACH</h2>
          <p className="intro-text">
            Beautiful landscapes don't require constant maintenance or excessive water. 
            Our naturalistic design approach creates outdoor spaces that work in harmony 
            with Idaho's environment—saving resources while enhancing your property's 
            natural beauty. This isn't about choosing between aesthetics and sustainability; 
            it's about achieving both through intelligent design.
          </p>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="section bg-clean-snow">
        <div className="section-inner">
          {coreFeatures.map((feature, index) => (
            <div key={index} className="naturalistic-feature">
              <div className="feature-content">
                <h3>{feature.title}</h3>
                <h4>{feature.subtitle}</h4>
                <p className="feature-description">{feature.text}</p>
                <ul className="feature-details">
                  {feature.details.map((detail, idx) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>
              <div className="feature-image">
                <img 
                  src={`/images/naturalistic-${index + 1}.jpg`} 
                  alt={feature.title}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Specialty Features */}
      <section className="section bg-white">
        <div className="section-inner">
          <h3 className="subsection-title text-center">SPECIALTY INSTALLATIONS</h3>
          <div className="specialty-grid">
            {specialtyFeatures.map((feature, index) => (
              <div key={index} className="specialty-card">
                <h4>{feature.title}</h4>
                <p>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Benefits */}
      <section className="section bg-evergreen-dark">
        <div className="section-inner">
          <h2 className="section-title text-center">LONG-TERM VALUE PROPOSITION</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>YEAR 1-2</h3>
              <p>Establishment period with regular maintenance as plants develop root systems</p>
            </div>
            <div className="benefit-card">
              <h3>YEAR 3-5</h3>
              <p>50% reduction in water usage, 70% reduction in mowing requirements</p>
            </div>
            <div className="benefit-card">
              <h3>YEAR 5+</h3>
              <p>Minimal inputs required, annual maintenance only for prairie areas</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NaturalisticDesignSection;